// Session lifecycle UI — sits at the layout level so it runs on every
// page once the user is authenticated.
//
// Two concerns, one component:
//   1. Idle timeout via useIdleTimeout — 30-min window by default,
//      per-instance override from /api/config (session_idle_minutes),
//      warning modal 2 minutes before force sign-out, then
//      auth.signoutRedirect() on expiry.
//   2. Forced sign-out from the server (401 session_superseded
//      because someone else signed in on this account, or
//      401 session_required because we don't have a session_id
//      cached). Renders a fullscreen banner with a Sign in button
//      that triggers a fresh sign-in flow.

import { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { setSessionForcedSignOutListener } from '@/lib/api';
import { beginSignOut, cognitoLogoutUrl } from '@/lib/auth-config';
import { clearSessionId } from '@/lib/session';
import { resetIdleTimer, useIdleTimeout } from '@/lib/useIdleTimeout';
import { useSessionIdleMinutes } from '@/lib/usePages';
import { buttonVariants } from '@/components/ui/Button';

/** Default idle window (minutes) when the instance sets no override. */
const IDLE_DEFAULT_MINUTES = 30;
/** Warning surfaces with 2 minutes left. */
const IDLE_WARNING_MS = 2 * 60 * 1000;

type ForcedReason = 'session_superseded' | 'session_required';

export function SessionWatcher() {
  const auth = useAuth();
  const signedIn = !!auth.user;

  const idleMinutes = useSessionIdleMinutes();
  const idle = useIdleTimeout({
    totalMs: (idleMinutes.data ?? IDLE_DEFAULT_MINUTES) * 60 * 1000,
    warningLeadMs: IDLE_WARNING_MS,
    enabled: signedIn,
  });

  const [forcedReason, setForcedReason] = useState<ForcedReason | null>(null);

  // Wire the api.ts listener once. The listener fires when an
  // authenticated request comes back with 401 session_superseded or
  // session_required.
  useEffect(() => {
    setSessionForcedSignOutListener((reason) => setForcedReason(reason));
    return () => setSessionForcedSignOutListener(null);
  }, []);

  // Reset forced state on a fresh sign-in.
  useEffect(() => {
    if (signedIn) setForcedReason(null);
  }, [signedIn]);

  // Idle expired → force sign-out via the Cognito-specific flow.
  //
  // react-oidc-context's auth.signoutRedirect() requires
  // end_session_endpoint in the OIDC discovery doc; Cognito doesn't
  // advertise one, so signoutRedirect produces a malformed URL that
  // lands the user on Cognito's "Required String parameter
  // 'client_id'" error page. The documented two-step pattern in
  // auth-config.ts: clear local oidc state, then hit Cognito's
  // /logout endpoint with the bare client_id we know.
  useEffect(() => {
    if (idle.phase === 'expired' && signedIn) {
      beginSignOut();
      clearSessionId();
      void auth.removeUser().finally(() => {
        window.location.href = cognitoLogoutUrl();
      });
    }
  }, [idle.phase, signedIn, auth]);

  // Forced reason set → clear local session_id so the next sign-in
  // mints a fresh one.
  useEffect(() => {
    if (forcedReason) clearSessionId();
  }, [forcedReason]);

  if (forcedReason) {
    return <ForcedSignOutBanner reason={forcedReason} onSignIn={() => auth.signinRedirect()} />;
  }

  if (idle.phase === 'warning' && signedIn) {
    return <IdleWarningModal expiresInSec={idle.expiresInSec} onStay={resetIdleTimer} />;
  }

  return null;
}

function IdleWarningModal({
  expiresInSec,
  onStay,
}: {
  expiresInSec: number;
  onStay: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Idle session warning"
    >
      <div className="w-full max-w-sm rounded-lg bg-card p-5 text-tres-text-primary shadow-xl">
        <h2 className="text-lg font-semibold">Still there?</h2>
        <p className="mt-2 text-sm text-tres-text-secondary">
          You'll be signed out in <span className="font-medium">{expiresInSec}s</span> due to
          inactivity.
        </p>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onStay}
            className={buttonVariants({ variant: 'primary', size: 'lg' })}
          >
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
}

function ForcedSignOutBanner({
  reason,
  onSignIn,
}: {
  reason: ForcedReason;
  onSignIn: () => void;
}) {
  const isSuperseded = reason === 'session_superseded';
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Signed out"
    >
      <div className="w-full max-w-sm rounded-lg bg-card p-5 text-tres-text-primary shadow-xl">
        <h2 className="text-lg font-semibold">
          {isSuperseded ? 'Signed out' : 'Sign in required'}
        </h2>
        <p className="mt-2 text-sm text-tres-text-secondary">
          {isSuperseded
            ? 'Your account signed in from another device or browser, so this session was ended.'
            : 'Your session is no longer valid. Please sign in again.'}
        </p>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onSignIn}
            className={buttonVariants({ variant: 'primary', size: 'lg' })}
          >
            Sign in
          </button>
        </div>
      </div>
    </div>
  );
}
